import { useState, useEffect } from "react";
import "@/App.css";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { AuthModal } from "@/components/AuthModal";
import { ResetPasswordModal } from "@/components/ResetPasswordModal";
import { HomeWaitlist } from "@/components/sections/HomeWaitlist";
import { SyllabusArchitect } from "@/components/sections/SyllabusArchitect";
import { SocraticTutor } from "@/components/sections/SocraticTutor";
import { EssayLab } from "@/components/sections/EssayLab";
import { GlobalImpact } from "@/components/sections/GlobalImpact";
import { AdminWaitlist } from "@/components/sections/AdminWaitlist";
import { PaymentSuccess, PaymentCancel } from "@/components/sections/PaymentResult";
import { AskAssistant } from "@/components/AskAssistant";
import { GraduationCap } from "lucide-react";

const INITIAL_TAB = () => {
  const v = new URLSearchParams(window.location.search).get("view");
  return ["home", "syllabus", "socratic", "essay", "impact", "admin"].includes(v) ? v : "home";
};

function Shell() {
  const [tab, setTab] = useState(INITIAL_TAB);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [resetToken, setResetToken] = useState(
    () => new URLSearchParams(window.location.search).get("reset") || null);

  const openAuth = (which) => { setAuthTab(which); setAuthOpen(true); };
  const navigate = (t) => { setTab(t); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const closeReset = () => {
    setResetToken(null);
    const params = new URLSearchParams(window.location.search);
    params.delete("reset");
    const qs = params.toString();
    window.history.replaceState({}, "", qs ? `/?${qs}` : "/");
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("welcome") === "1") {
      toast.success("Welcome to OCC Premium — your dashboard is unlocked.");
      window.history.replaceState({}, "", "/");
    }
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header active={tab} onNavigate={navigate} onAuth={openAuth} />
      <main className="flex-1">
        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
            {tab === "home" && <HomeWaitlist onNavigate={navigate} />}
            {tab === "syllabus" && <SyllabusArchitect onAuth={openAuth} onNavigate={navigate} />}
            {tab === "socratic" && <SocraticTutor onAuth={openAuth} onNavigate={navigate} />}
            {tab === "essay" && <EssayLab onAuth={openAuth} onNavigate={navigate} />}
            {tab === "impact" && <GlobalImpact />}
            {tab === "admin" && <AdminWaitlist />}
          </motion.div>
        </AnimatePresence>
      </main>

      <footer className="bg-navy-deep text-slate-400 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-md bg-white/5 flex items-center justify-center">
              <GraduationCap className="h-4 w-4 text-persian-blue" />
            </div>
            <div className="text-sm">
              <span className="font-serif text-white">The Organization for Cognitive Capital</span>
              <span className="block text-xs">Academic excellence · Global equity</span>
            </div>
          </div>
          <p className="text-xs">© {new Date().getFullYear()} OCC. Non-monetary asset distribution across the US, Iran & Afghanistan.</p>
        </div>
      </footer>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultTab={authTab} />
      {resetToken && <ResetPasswordModal token={resetToken} onDone={closeReset} />}
      <AskAssistant />
    </div>
  );
}

export default function App() {
  const path = window.location.pathname;
  if (path.startsWith("/payment/success")) {
    return (
      <AuthProvider>
        <PaymentSuccess />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    );
  }
  if (path.startsWith("/payment/cancel")) {
    return (
      <>
        <PaymentCancel />
        <Toaster position="top-right" richColors />
      </>
    );
  }
  return (
    <AuthProvider>
      <Shell />
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}
